import { useEffect } from 'react';

// Shared state across all instances of the hook - using module-level ref
let wasRecentlyHiddenRef = { current: false };
let resetTimer = null;
let visibilityListenerInitialized = false;

/**
 * Hook to detect page visibility changes (tab switching)
 * Uses shared ref so all instances see the same visibility state without causing re-renders
 * Returns:
 * - checkWasRecentlyHidden: function that returns true if page was just made visible
 */
const usePageVisibility = () => {
  useEffect(() => {
    // Only initialize the listener once globally
    if (visibilityListenerInitialized) {
      return;
    }

    const handleVisibilityChange = () => {
      const nowVisible = !document.hidden;

      if (nowVisible) {
        // Page just became visible (tab was selected)
        wasRecentlyHiddenRef.current = true;

        // Reset the flag after a short delay to allow all hooks to see it
        if (resetTimer) {
          clearTimeout(resetTimer);
        }
        resetTimer = setTimeout(() => {
          wasRecentlyHiddenRef.current = false;
        }, 100);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerInitialized = true;

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (resetTimer) {
        clearTimeout(resetTimer);
      }
      visibilityListenerInitialized = false;
    };
  }, []);

  // Return a function that checks the ref without causing re-renders
  const checkWasRecentlyHidden = () => {
    return wasRecentlyHiddenRef.current;
  };

  return {
    checkWasRecentlyHidden
  };
};

export default usePageVisibility;
